import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import { PageContainer } from '@/components/layout'
import { Card, Spinner, Button, ToastContainer, useToast } from '@/components/ui'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import type { PostWithMetrics, AnalyticsListResponse } from '@/services/types'
import { getItemPlatform, getItemPostUrl, getPlatformDisplayName } from '@/services/platforms'

export function AnalyticsPage() {
  const { toasts, addToast, dismissToast } = useToast()
  const [analytics, setAnalytics] = useState<AnalyticsListResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshingId, setRefreshingId] = useState<number | null>(null)

  const loadAnalytics = useCallback(async () => {
    try {
      setLoadError(null)
      const data = await api.getAnalytics()
      setAnalytics(data)
    } catch (err) {
      const message = err instanceof ApiError ? getErrorMessage(err, 'Failed to load analytics') : 'Failed to load analytics'
      setLoadError(message)
      addToast(message, 'error')
    } finally {
      setIsLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  const handleRefreshMetrics = async (id: number) => {
    setRefreshingId(id)
    try {
      await api.refreshMetrics(id)
      addToast('Metrics refreshed', 'success')
      loadAnalytics()
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to refresh metrics') : 'Failed to refresh metrics', 'error')
    } finally {
      setRefreshingId(null)
    }
  }

  if (isLoading) {
    return (
      <PageContainer title="Analytics">
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (loadError && !analytics) {
    return (
      <PageContainer title="Analytics">
        <Card>
          <div className="text-center py-8">
            <p className="text-error mb-4">{loadError}</p>
            <Button onClick={loadAnalytics}>Try Again</Button>
          </div>
        </Card>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </PageContainer>
    )
  }

  if (!analytics) {
    return (
      <PageContainer title="Analytics">
        <div className="text-center py-12">
          <p className="text-secondary-text">No analytics data available.</p>
        </div>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </PageContainer>
    )
  }

  return (
    <PageContainer title="Analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <p className="text-sm text-secondary-text">Total Posts</p>
          <p className="text-2xl font-medium">{analytics.total_posts ?? 0}</p>
        </Card>
        <Card>
          <p className="text-sm text-secondary-text">Total Impressions</p>
          <p className="text-2xl font-medium">{(analytics.total_impressions ?? 0).toLocaleString()}</p>
        </Card>
        <Card>
          <p className="text-sm text-secondary-text">Total Likes</p>
          <p className="text-2xl font-medium">{(analytics.total_likes ?? 0).toLocaleString()}</p>
        </Card>
        <Card>
          <p className="text-sm text-secondary-text">Avg Engagement</p>
          <p className="text-2xl font-medium">{(analytics.avg_engagement_rate ?? 0).toFixed(2)}%</p>
        </Card>
      </div>

      {/* Posts List */}
      <h2 className="text-lg font-medium mb-4">Posted Content</h2>

      {(analytics.posts ?? []).length === 0 ? (
        <div className="text-center py-12">
          <p className="text-secondary-text">No posted content yet. Post some content to see analytics!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(analytics.posts ?? []).map((post: PostWithMetrics) => (
            <Card key={post.id}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm mb-3">{post.content_text}</p>

                  {post.metrics && (
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center border-t border-border pt-3">
                      <div>
                        <p className="text-xs text-secondary-text">Impressions</p>
                        <p className="font-medium">{post.metrics.impressions.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-text">Likes</p>
                        <p className="font-medium">{post.metrics.likes.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-text">Reposts</p>
                        <p className="font-medium">{post.metrics.retweets.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-text">Replies</p>
                        <p className="font-medium">{post.metrics.replies.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-text">Bookmarks</p>
                        <p className="font-medium">{post.metrics.bookmarks.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-text">Engagement</p>
                        <p className="font-medium">{post.metrics.engagement_rate.toFixed(2)}%</p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-secondary-text mt-3">
                    Posted {post.posted_at && format(new Date(post.posted_at), 'MMM d, yyyy h:mm a')}
                    {post.metrics && (
                      <> · Metrics updated {format(new Date(post.metrics.fetched_at), 'MMM d, h:mm a')}</>
                    )}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => handleRefreshMetrics(post.id)}
                    loading={refreshingId === post.id}
                  >
                    Refresh
                  </Button>
                  {(() => {
                    const viewUrl = getItemPostUrl(post)
                    const platformName = getPlatformDisplayName(getItemPlatform(post))
                    return viewUrl ? (
                      <Button
                        variant="secondary"
                        onClick={() => window.open(viewUrl, '_blank')}
                      >
                        View on {platformName}
                      </Button>
                    ) : null
                  })()}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </PageContainer>
  )
}
